/**
 * Cosmetic radial-distance fog (issue #60 supersedes the flat linear
 * `Fog` this replaced; issue #175 adds camera-distance-aware thinning).
 * GOAL.md: "Atmosphere and distance fade are cosmetic only and must never
 * influence any LOS result or its display." This module only ever touches
 * THREE.FogExp2 on the render scene — it has no reference to, and no way
 * to reach, any LOS/classifier code path.
 *
 * Stock THREE.FogExp2 uses radial distance from the camera (no height
 * term). Ground-distance fog (terrain-following haze) would need a custom
 * onBeforeCompile shader — deferred follow-up, out of scope for #175.
 *
 * Colour comes from scene/atmosphere.ts's single palette (the same source
 * scene/sky.ts's horizon gradient stop and scene/lighting.ts's sun tint
 * use), so fog and sky never show a seam at the horizon.
 *
 * Density picked against this AOI's real viewing distances: the turbine
 * array centroid sits ~3.7 km from the default viewpoint
 * (data/config/viewpoints.json's bearing/coordinates vs. the array centroid
 * noted there), and the tile loader streams terrain out to a 10-12 km
 * radius (LOAD_RADIUS_M/UNLOAD_RADIUS_M in main.ts). At
 * FOG_DENSITY = 0.00008, three's exp-squared visibility factor
 * `exp(-(density*distance)^2)` is ~0.97 at 2 km (near turbines essentially
 * unhazed), ~0.53 at 10 km (far turbines still legible through haze —
 * the "2-10 km remain legible" acceptance criterion), and ~0.13 at 18 km
 * (fully hazed into the horizon, matching the sky dome's horizon colour so
 * there's no seam). Issue #175 thins density when the camera pulls back
 * from the active scenario's array centroid so proposed turbines stay
 * legible at far viewpoints (~9.5 km) without changing near framing.
 */
import { FogExp2, Scene } from 'three';
import { ATMOSPHERE_PALETTE, rgbToHex } from './atmosphere';

/** Base/near density — unchanged at default framing (~3.7 km camera-to-array). */
export const FOG_DENSITY = 0.00008;

/** Camera-to-array distance (m) at which base density applies; matches default viewpoint. */
export const FOG_REFERENCE_DISTANCE_M = 3700;

/** Never fully clear fog at extreme pull-back (camera far plane ~40 km). */
export const FOG_MIN_DENSITY = 0.00003;

/** Thinning exponent: at 9.5 km camera-to-array, visibility rises from ~0.55 to >= 0.75. */
const FOG_THIN_EXPONENT = 0.37;

const DENSITY_EPSILON = 1e-10;

/**
 * Effective FogExp2 density for a given camera-to-array-centroid distance.
 * Near the array (<= reference) returns base density; farther out thins
 * exponentially so distant turbines stay legible. Clamped, never NaN/negative.
 */
export function effectiveFogDensity(cameraToArrayM: number): number {
  if (!Number.isFinite(cameraToArrayM) || cameraToArrayM <= FOG_REFERENCE_DISTANCE_M) {
    return FOG_DENSITY;
  }
  const thinned = FOG_DENSITY * (FOG_REFERENCE_DISTANCE_M / cameraToArrayM) ** FOG_THIN_EXPONENT;
  return Math.max(FOG_MIN_DENSITY, thinned);
}

export function applyDistanceFade(scene: Scene): void {
  scene.fog = new FogExp2(rgbToHex(ATMOSPHERE_PALETTE.horizon), FOG_DENSITY);
}

/**
 * Updates scene fog density when camera-to-array distance changes meaningfully.
 * Epsilon compare avoids redundant writes; no allocation, no ground queries.
 */
export function updateDistanceFadeDensity(scene: Scene, cameraToArrayM: number): void {
  const fog = scene.fog;
  if (!(fog instanceof FogExp2)) return;
  const next = effectiveFogDensity(cameraToArrayM);
  if (Math.abs(fog.density - next) > DENSITY_EPSILON) {
    fog.density = next;
  }
}
